import { useEffect, useMemo, useRef, useState } from 'react';

// Common international dial codes, covering the business geographies this app's vendors/contacts/team
// members are realistically based in. Not exhaustive by design -- a typed code not in this list (e.g.
// "+256") is still accepted as-is, so the field is never a hard gate on this list.
export interface CountryDial { iso: string; name: string; dial: string }

export const COUNTRY_DIALS: CountryDial[] = [
  { iso: 'US', name: 'United States', dial: '+1' },
  { iso: 'CA', name: 'Canada', dial: '+1' },
  { iso: 'GB', name: 'United Kingdom', dial: '+44' },
  { iso: 'IN', name: 'India', dial: '+91' },
  { iso: 'AU', name: 'Australia', dial: '+61' },
  { iso: 'DE', name: 'Germany', dial: '+49' },
  { iso: 'FR', name: 'France', dial: '+33' },
  { iso: 'IT', name: 'Italy', dial: '+39' },
  { iso: 'ES', name: 'Spain', dial: '+34' },
  { iso: 'NL', name: 'Netherlands', dial: '+31' },
  { iso: 'BE', name: 'Belgium', dial: '+32' },
  { iso: 'CH', name: 'Switzerland', dial: '+41' },
  { iso: 'AT', name: 'Austria', dial: '+43' },
  { iso: 'SE', name: 'Sweden', dial: '+46' },
  { iso: 'NO', name: 'Norway', dial: '+47' },
  { iso: 'DK', name: 'Denmark', dial: '+45' },
  { iso: 'FI', name: 'Finland', dial: '+358' },
  { iso: 'IE', name: 'Ireland', dial: '+353' },
  { iso: 'PT', name: 'Portugal', dial: '+351' },
  { iso: 'PL', name: 'Poland', dial: '+48' },
  { iso: 'CZ', name: 'Czech Republic', dial: '+420' },
  { iso: 'RO', name: 'Romania', dial: '+40' },
  { iso: 'GR', name: 'Greece', dial: '+30' },
  { iso: 'RU', name: 'Russia', dial: '+7' },
  { iso: 'TR', name: 'Turkey', dial: '+90' },
  { iso: 'UA', name: 'Ukraine', dial: '+380' },
  { iso: 'IL', name: 'Israel', dial: '+972' },
  { iso: 'AE', name: 'United Arab Emirates', dial: '+971' },
  { iso: 'SA', name: 'Saudi Arabia', dial: '+966' },
  { iso: 'QA', name: 'Qatar', dial: '+974' },
  { iso: 'EG', name: 'Egypt', dial: '+20' },
  { iso: 'ZA', name: 'South Africa', dial: '+27' },
  { iso: 'NG', name: 'Nigeria', dial: '+234' },
  { iso: 'KE', name: 'Kenya', dial: '+254' },
  { iso: 'CN', name: 'China', dial: '+86' },
  { iso: 'JP', name: 'Japan', dial: '+81' },
  { iso: 'KR', name: 'South Korea', dial: '+82' },
  { iso: 'HK', name: 'Hong Kong', dial: '+852' },
  { iso: 'TW', name: 'Taiwan', dial: '+886' },
  { iso: 'SG', name: 'Singapore', dial: '+65' },
  { iso: 'MY', name: 'Malaysia', dial: '+60' },
  { iso: 'ID', name: 'Indonesia', dial: '+62' },
  { iso: 'TH', name: 'Thailand', dial: '+66' },
  { iso: 'VN', name: 'Vietnam', dial: '+84' },
  { iso: 'PH', name: 'Philippines', dial: '+63' },
  { iso: 'PK', name: 'Pakistan', dial: '+92' },
  { iso: 'BD', name: 'Bangladesh', dial: '+880' },
  { iso: 'LK', name: 'Sri Lanka', dial: '+94' },
  { iso: 'NZ', name: 'New Zealand', dial: '+64' },
  { iso: 'MX', name: 'Mexico', dial: '+52' },
  { iso: 'BR', name: 'Brazil', dial: '+55' },
  { iso: 'AR', name: 'Argentina', dial: '+54' },
  { iso: 'CL', name: 'Chile', dial: '+56' },
  { iso: 'CO', name: 'Colombia', dial: '+57' },
  { iso: 'PE', name: 'Peru', dial: '+51' },
];

export const KNOWN_DIALS = Array.from(new Set(COUNTRY_DIALS.map(c => c.dial)));

// Typical national significant-number length (digits only, after the country code, e.g. India's
// "98765 43210" = 10) for each listed country -- so the field can stop a vendor from typing, say, 14
// digits after picking India. These are the common/standard lengths each country's own numbering
// plan uses for ordinary mobile/landline numbers, not a hard worldwide-verified rule (some countries
// allow more than one valid length) -- a country not in this table has no cap (generous ITU max of 15
// is used instead), so an uncommon real number is never blocked outright.
const NATIONAL_NUMBER_LENGTH: Record<string, number> = {
  US: 10, CA: 10, GB: 10, IN: 10, AU: 9, DE: 11, FR: 9, IT: 10, ES: 9, NL: 9,
  BE: 9, CH: 9, AT: 11, SE: 9, NO: 8, DK: 8, FI: 10, IE: 9, PT: 9, PL: 9,
  CZ: 9, RO: 9, GR: 10, RU: 10, TR: 10, UA: 9, IL: 9, AE: 9, SA: 9, QA: 8,
  EG: 10, ZA: 9, NG: 10, KE: 9, CN: 11, JP: 10, KR: 10, HK: 8, TW: 9, SG: 8,
  MY: 10, ID: 11, TH: 9, VN: 9, PH: 10, PK: 10, BD: 10, LK: 9, NZ: 9, MX: 10,
  BR: 11, AR: 10, CL: 9, CO: 10, PE: 9,
};
const DEFAULT_MAX_DIGITS = 15; // ITU E.164's own overall max, used for an unlisted/custom code.

export function maxDigitsFor(iso: string | undefined): number {
  return (iso && NATIONAL_NUMBER_LENGTH[iso]) || DEFAULT_MAX_DIGITS;
}

/** Keeps every character the vendor typed (spaces, dashes, parens -- whatever formatting they used)
 * but stops counting once `maxDigits` real digits have been seen, dropping any digit beyond that --
 * so "987654321099" for India (max 10) becomes "9876543210", while a still-forming "987 65" is left
 * alone rather than fighting the vendor mid-type. */
export function limitPhoneDigits(raw: string, maxDigits: number): string {
  let out = '';
  let digits = 0;
  for (const ch of raw) {
    if (ch >= '0' && ch <= '9') {
      if (digits >= maxDigits) continue;
      digits++;
    }
    out += ch;
  }
  return out;
}

export function digitCount(raw: string): number {
  let n = 0;
  for (const ch of raw) if (ch >= '0' && ch <= '9') n++;
  return n;
}

export function splitPhoneValue(value: string): { dial: string; rest: string } {
  const v = (value || '').trim();
  if (!v) return { dial: '', rest: '' };
  if (!v.startsWith('+')) return { dial: '', rest: v };
  const match = v.match(/^(\+\d{1,4})(?:[\s-]+|(?=\d))(.*)$/);
  if (match) return { dial: match[1], rest: match[2].trim() };
  return { dial: v, rest: '' };
}

export function combinePhone(dial: string, rest: string): string {
  const d = dial.trim();
  const r = rest.trim();
  return d && r ? `${d} ${r}` : d || r;
}

/** A typed code is used as-is if it already starts with "+"; a bare number gets "+" prepended
 * (e.g. typing "256" alone is treated the same as "+256"); anything else is kept as typed rather
 * than silently discarded, since the field must never block an entry just because it's unrecognized. */
export function normalizeDial(raw: string): string {
  const v = raw.trim();
  if (!v) return '';
  if (v.startsWith('+')) return v;
  if (/^\d+$/.test(v)) return `+${v}`;
  return v;
}

/** A real flag icon for a 2-letter ISO country code, from flagcdn.com -- the same "small external
 * image, no asset to ship" pattern this app already uses for vendor/tenant logos (Google's favicon
 * service, see VendorLogo/TenantLogo). Deliberately NOT a Unicode flag emoji: Windows' emoji font has
 * no flag glyphs at all, so Chrome/Edge on Windows render regional-indicator emoji as plain two-letter
 * codes ("US", "IN") instead of a flag -- confirmed live, not just a theoretical gap. An actual <img>
 * renders identically on every OS. Falls back to a globe emoji only for an unrecognized/empty code, or
 * if the image itself fails to load (offline, flagcdn down). */
export function FlagIcon({ iso, width = 18 }: { iso: string | undefined; width?: number }) {
  const code = (iso || '').toLowerCase();
  if (code.length !== 2) return <span aria-hidden style={{ fontSize: width * 0.8, lineHeight: 1 }}>🌐</span>;
  return (
    <img
      src={`https://flagcdn.com/w40/${code}.png`}
      srcSet={`https://flagcdn.com/w40/${code}.png 1x, https://flagcdn.com/w80/${code}.png 2x`}
      alt=""
      aria-hidden
      width={width}
      style={{ display: 'inline-block', height: 'auto', verticalAlign: 'middle', borderRadius: 2, boxShadow: '0 0 0 1px rgba(0,0,0,0.1)' }}
      onError={e => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
    />
  );
}

export function filterCountryDials(query: string): CountryDial[] {
  const q = query.trim().toLowerCase().replace(/^\+/, '');
  if (!q) return COUNTRY_DIALS;
  return COUNTRY_DIALS.filter(c =>
    c.dial.toLowerCase().includes(q) || c.dial.replace('+', '').includes(q) ||
    c.name.toLowerCase().includes(q) || c.iso.toLowerCase().includes(q));
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  placeholder?: string;
  defaultDial?: string;
  selectStyle?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  wrapperStyle?: React.CSSProperties;
}

/** Country-code + phone-number pair that hands the parent a single combined string (e.g.
 * "+91 98765 43210"), so every existing `contact_phone`/`phone`/`phone_number` TEXT column needs no
 * backend change. The country code is a searchable combobox, not a plain <select> (which can't be
 * typed into to filter, and whose native type-ahead only matches each option's start, not a dial
 * code or country name) -- clicking it opens the full list, typing narrows it by name/ISO/dial code,
 * and a typed value that matches nothing is still accepted directly as a custom code.
 *
 * dial/rest/query are local state seeded once from `value` (not re-derived every render), so a
 * mid-typing search string isn't fought by a parent re-render; `value` changing externally (e.g. the
 * parent clears the whole form) still resyncs via the effect below. */
export default function PhoneNumberInput({
  value, onChange, id, placeholder, defaultDial = '+1', selectStyle, inputStyle, wrapperStyle,
}: Props) {
  const initial = useMemo(() => splitPhoneValue(value), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [dial, setDial] = useState(initial.dial || defaultDial);
  const [rest, setRest] = useState(initial.rest);
  const [query, setQuery] = useState(initial.dial || defaultDial);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(false); // false = just opened, not yet typed -> show full list
  const [highlight, setHighlight] = useState(0);
  const lastEmitted = useRef(value);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value === lastEmitted.current) return;
    const parsed = splitPhoneValue(value);
    const d = parsed.dial || defaultDial;
    setDial(d);
    setRest(parsed.rest);
    setQuery(d);
    lastEmitted.current = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery(dial);
        setTyped(false);
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open, dial]);

  const emit = (d: string, r: string) => {
    const combined = combinePhone(d, r);
    lastEmitted.current = combined;
    onChange(combined);
  };

  const filtered = useMemo(() => (typed ? filterCountryDials(query) : COUNTRY_DIALS), [typed, query]);

  const pick = (c: CountryDial) => {
    setDial(c.dial);
    setQuery(c.dial);
    setOpen(false);
    setTyped(false);
    emit(c.dial, rest);
  };

  const commitTyped = () => {
    const d = normalizeDial(query) || dial;
    setDial(d);
    setQuery(d);
    setOpen(false);
    setTyped(false);
    emit(d, rest);
  };

  const baseInputStyle: React.CSSProperties = {
    border: '1px solid #D7D2C4', borderRadius: 8, padding: '8px 10px', fontSize: 13.5, fontFamily: 'inherit',
    ...inputStyle,
  };

  const currentIso = COUNTRY_DIALS.find(c => c.dial === dial)?.iso;

  return (
    <div style={{ display: 'flex', gap: 6, ...wrapperStyle }}>
      <div ref={boxRef} style={{ position: 'relative', width: 82, flexShrink: 0 }}>
        <span aria-hidden style={{
          position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
          lineHeight: 1, pointerEvents: 'none', opacity: open && typed ? 0.35 : 1,
        }}>
          <FlagIcon iso={currentIso} width={15} />
        </span>
        <input
          aria-label="Country code"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          autoComplete="off"
          value={query}
          placeholder="+1"
          onFocus={() => { setOpen(true); setTyped(false); setHighlight(0); setQuery(''); }}
          onChange={e => {
            let next = e.target.value;
            // Defends against a focus/clear race: if the field wasn't actually empty yet when this
            // keystroke landed, the browser appended onto the still-displayed dial code instead of
            // replacing it (e.g. "+91" + "256" -> "+91256") -- strip that stale prefix off.
            if (!typed && query && next.startsWith(query) && next.length > query.length) {
              next = next.slice(query.length);
            }
            setQuery(next);
            setOpen(true);
            setTyped(true);
            setHighlight(0);
          }}
          onKeyDown={e => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHighlight(h => Math.min(h + 1, filtered.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
            else if (e.key === 'Enter') {
              e.preventDefault();
              if (open && typed && filtered[highlight]) pick(filtered[highlight]);
              else if (open && typed) commitTyped();
              else { setOpen(false); setQuery(dial); }
            } else if (e.key === 'Escape') {
              setOpen(false); setQuery(dial); setTyped(false);
            }
          }}
          style={{ width: '100%', ...baseInputStyle, padding: '8px 6px 8px 25px', ...selectStyle }}
        />
        {open && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, minWidth: 220, zIndex: 20, maxHeight: 230, overflowY: 'auto',
            background: '#fff', border: '1px solid #D7D2C4', borderRadius: 8, marginTop: 4, boxShadow: '0 6px 18px rgba(0,0,0,0.14)',
          }}>
            {filtered.length === 0 ? (
              <div
                onMouseDown={e => { e.preventDefault(); commitTyped(); }}
                style={{ padding: '8px 10px', fontSize: 12.5, color: '#5B6472', cursor: 'pointer' }}
              >
                No match — use "{normalizeDial(query) || query}"
              </div>
            ) : filtered.map((c, i) => (
              <div
                key={c.iso}
                onMouseDown={e => { e.preventDefault(); pick(c); }}
                onMouseEnter={() => setHighlight(i)}
                style={{
                  padding: '7px 10px', fontSize: 13, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 10,
                  background: i === highlight ? '#F5F2E9' : 'transparent',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 7, overflow: 'hidden' }}>
                  <span style={{ flexShrink: 0, display: 'inline-flex' }}><FlagIcon iso={c.iso} width={16} /></span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                </span>
                <span style={{ color: '#8A8372', flexShrink: 0 }}>{c.dial}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <input
        id={id}
        aria-label="Phone number"
        value={rest}
        placeholder={placeholder}
        maxLength={30}
        style={{ flex: 1, minWidth: 0, ...baseInputStyle }}
        onChange={e => {
          const limited = limitPhoneDigits(e.target.value, maxDigitsFor(currentIso));
          setRest(limited);
          emit(dial, limited);
        }}
      />
    </div>
  );
}
